/** Session-only undo and redo history of browser-held design changes for the builder. */
import type { StateCreator } from 'zustand'
import type { DesignChange } from './designHistoryLabels.types'
import type { DesignHistoryState } from './types'

// Caps memory for a long session; older edits drop off the bottom.
export const DESIGN_HISTORY_LIMIT = 50

export interface DesignHistorySlice {
  designHistory: DesignHistoryState
  recordDesignRevision: (configId: string, change: DesignChange) => void
  completeDesignUndo: (configId: string, entryId: string) => void
  completeDesignRedo: (configId: string, entryId: string) => void
  clearDesignHistory: (configId?: string) => void
}

export const EMPTY_DESIGN_HISTORY: DesignHistoryState = {
  configId: null,
  past: [],
  future: [],
  restoreCount: 0,
}

type HistoryList = 'past' | 'future'

// Moves the newest entry of one list onto the other, only if it is still the expected entry.
function moveEntry(
  history: DesignHistoryState,
  configId: string,
  entryId: string,
  from: HistoryList,
  to: HistoryList
): Partial<DesignHistorySlice> {
  const entry = history[from][history[from].length - 1]

  if (history.configId !== configId || entry?.id !== entryId) return {}

  return {
    designHistory: {
      ...history,
      [from]: history[from].slice(0, -1),
      [to]: [...history[to], entry],
      restoreCount: history.restoreCount + 1,
    },
  }
}

export const createDesignHistorySlice: StateCreator<
  DesignHistorySlice,
  [],
  [],
  DesignHistorySlice
> = (set) => ({
  designHistory: EMPTY_DESIGN_HISTORY,

  recordDesignRevision: (configId, change) =>
    set(({ designHistory }) => {
      const past = designHistory.configId === configId ? designHistory.past : []

      return {
        designHistory: {
          configId,
          past: [...past, { ...change, id: crypto.randomUUID() }].slice(
            -DESIGN_HISTORY_LIMIT
          ),
          future: [],
          restoreCount: designHistory.restoreCount,
        },
      }
    }),

  completeDesignUndo: (configId, entryId) =>
    set(({ designHistory }) =>
      moveEntry(designHistory, configId, entryId, 'past', 'future')
    ),

  completeDesignRedo: (configId, entryId) =>
    set(({ designHistory }) =>
      moveEntry(designHistory, configId, entryId, 'future', 'past')
    ),

  clearDesignHistory: (configId) =>
    set(({ designHistory }) =>
      configId && designHistory.configId !== configId
        ? {}
        : {
            designHistory: {
              ...EMPTY_DESIGN_HISTORY,
              restoreCount: designHistory.restoreCount,
            },
          }
    ),
})
